import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { Action, Reducer } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import { authReducer } from 'Auth';
import { breadcrumbReducer } from 'Breadcrumb';
import { configReducer } from 'Config';
import { axiosMiddleware } from 'Config/axios';
import { coursesReducer } from 'Courses/data';
import { userReducer } from 'User';
import { isNotProductionEnvironment } from 'Utils/environments';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'user', 'config']
};

const rootReducer = combineReducers({
  config: configReducer,
  auth: authReducer,
  user: userReducer,
  courses: coursesReducer,
  breadcrumb: breadcrumbReducer
});

export type RootState = ReturnType<typeof rootReducer>;

const resettableRootReducer: Reducer<RootState> = (
  state: RootState | undefined,
  action: Action
) => {
  if (action.type === 'auth/logout/pending') {
    storage.removeItem('persist:root');
    return rootReducer(undefined, action);
  }

  return rootReducer(state, action);
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer();

export const store = configureStore({
  reducer: persistReducer(persistConfig, resettableRootReducer),
  devTools: isNotProductionEnvironment(),
  enhancers: getDefaultEnhancers => {
    const enhancers = getDefaultEnhancers();

    if (!isNotProductionEnvironment()) {
      return enhancers.concat(sentryReduxEnhancer);
    }

    return enhancers;
  },
  middleware: getDefaultMiddleware => [
    axiosMiddleware,
    ...getDefaultMiddleware({
      serializableCheck: {
        ignoredPaths: ['_persist'],
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE']
      }
    })
  ]
} as any);

export const persistor = persistStore(store);
export type AppDispatch = typeof store.dispatch;
